import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../services/CustomerAuthContext';
import MembershipBadge from '../components/MembershipBadge';
import { listCommissions, createCommission, deleteCommission, openSubscriptionPortal, createSubscriptionCheckout, getCustomerOrders, requestCancelOrder, getContributions, getPitches, acceptPitch, getCustomerOffers, offerCheckout } from '../services/api';
import Header from '../components/Header';

function CustomerContributions() {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    getContributions().then(setData).catch(() => {});
  }, []);

  if (!data) return <p className="empty-state">Loading...</p>;

  return (
    <div className="contributions-section">
      <div className="contrib-stats">
        <div className="contrib-stat"><span className="contrib-num">{data.stats.variationsMade}</span><span className="contrib-label">Variations Made</span></div>
        <div className="contrib-stat"><span className="contrib-num">{data.stats.shopResultsPosted}</span><span className="contrib-label">Builds Posted</span></div>
        <div className="contrib-stat"><span className="contrib-num">{data.stats.totalStars}</span><span className="contrib-label">Stars Received</span></div>
      </div>
      {data.badges.length > 0 && (
        <div className="contrib-badges">
          {data.badges.map((b, i) => <span className="contrib-badge" key={i}>{b.icon} {b.name}</span>)}
        </div>
      )}
      {data.variations.length > 0 && (
        <div className="contrib-list">
          <h4>My Variations</h4>
          {data.variations.map(bp => (
            <div className="contrib-item" key={bp.blueprintId}>
              <span>{bp.title}</span>
              <span className="contrib-item-stats">★{bp.starCount || 0}</span>
            </div>
          ))}
        </div>
      )}
      {data.shopResults.length > 0 && (
        <div className="contrib-list">
          <h4>My Shop Results</h4>
          {data.shopResults.map(r => (
            <div className="contrib-item" key={r.resultId}>
              <span>{r.notes?.slice(0, 60) || 'Build posted'}</span>
              <span className="contrib-item-stats">{new Date((r.createdAt || 0) * 1000).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
      {data.stats.variationsMade === 0 && data.stats.shopResultsPosted === 0 && (
        <p className="empty-state">No contributions yet. Visit the Workshop to create variations or post build results!</p>
      )}
    </div>
  );
}

function CustomerOffers() {
  const [offers, setOffers] = React.useState([]);
  React.useEffect(() => {
    getCustomerOffers().then(data => setOffers(data.offers || [])).catch(() => {});
  }, []);
  return (
    <div>
      {offers.length === 0 && <p className="empty-state">No offers submitted yet. Make an offer on any item in a vendor's booth!</p>}
      {offers.map(o => (
        <div className="order-card" key={o.offerId}>
          <div className="order-card-info">
            <h4>{o.itemName}</h4>
            <p className="order-price">Your offer: ${(o.offerAmount / 100).toFixed(2)} <span style={{color:'var(--text-muted)'}}>(listed: ${(o.listPrice / 100).toFixed(2)})</span></p>
            <p className="order-meta">{new Date((o.createdAt || 0) * 1000).toLocaleDateString()}</p>
            <span className={`order-status ${o.status}`}>{o.status === 'accepted' ? '✅ Accepted' : o.status === 'declined' ? '❌ Declined' : '⏳ Pending'}</span>
            {o.status === 'accepted' && (
              <button className="btn-primary" style={{marginTop:'0.75rem'}} onClick={async () => {
                try { const data = await offerCheckout(o.offerId); if (data.url) window.location.href = data.url; }
                catch (e) { alert(e.error || 'Failed to create checkout'); }
              }}>Pay Now — ${(o.offerAmount / 100).toFixed(2)}</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomerOrders() {
  const [orders, setOrders] = React.useState([]);
  const [refundOrder, setRefundOrder] = React.useState(null);
  const [refundForm, setRefundForm] = React.useState({ reason: '', description: '', images: [] });
  const [uploadingRefundImg, setUploadingRefundImg] = React.useState(false);

  React.useEffect(() => {
    getCustomerOrders().then(data => setOrders(data.orders || [])).catch(() => {});
  }, []);

  async function handleCancel(orderId) {
    if (!window.confirm('Request cancellation for this order? The vendor has 24 hours to respond.')) return;
    try {
      await requestCancelOrder(orderId);
      setOrders(prev => prev.map(o => o.orderId === orderId ? { ...o, status: 'cancellation_requested' } : o));
    } catch (err) { alert(err.error || 'Failed to request cancellation'); }
  }

  const statusLabel = (status) => {
    switch(status) {
      case 'pending_fulfillment': return { text: '⏳ Awaiting Shipment', cls: 'pending' };
      case 'cancellation_requested': return { text: '🔄 Cancellation Pending', cls: 'pending' };
      case 'shipped': return { text: '✅ Shipped', cls: 'shipped' };
      case 'cancelled': return { text: '❌ Cancelled', cls: 'cancelled' };
      case 'expired': return { text: '⏰ Expired', cls: 'cancelled' };
      default: return { text: status, cls: '' };
    }
  };

  return (
    <div className="orders-tab">
      {orders.length === 0 && <p className="empty-state">No orders yet. When you purchase an item, it will appear here.</p>}
      {orders.map(order => {
        const s = statusLabel(order.status);
        return (
          <div className="order-card" key={order.orderId}>
            <div className="order-card-image">
              {order.itemImage && <img src={order.itemImage} alt={order.itemName} />}
            </div>
            <div className="order-card-info">
              <h4>{order.itemName}</h4>
              <p className="order-price">${((order.amount || 0) / 100).toFixed(2)}</p>
              <p className="order-meta">Ordered: {new Date((order.createdAt || 0) * 1000).toLocaleDateString()}</p>
              <span className={`order-status ${s.cls}`}>{s.text}</span>
              {order.trackingNumber && (
                <p className="order-meta">Tracking: {order.trackingNumber} ({order.trackingCarrier})</p>
              )}
              {order.status === 'pending_fulfillment' && (
                <button className="btn-delete" onClick={() => handleCancel(order.orderId)} style={{marginTop:'0.75rem'}}>Request Cancellation</button>
              )}
              {(order.status === 'shipped' || order.status === 'in_production') && (
                <button className="btn-secondary" onClick={() => setRefundOrder(order)} style={{marginTop:'0.75rem'}}>Request Refund</button>
              )}
              {order.status === 'cancellation_requested' && (
                <p className="order-meta" style={{marginTop:'0.5rem'}}>Vendor has 24 hours to respond to your cancellation request.</p>
              )}
            </div>
          </div>
        );
      })}

      {refundOrder && (
        <div className="item-form-overlay">
          <div className="item-form">
            <h3>Request Refund</h3>
            <p className="settings-hint">Item: {refundOrder.itemName} — ${((refundOrder.amount || 0) / 100).toFixed(2)}</p>
            <select value={refundForm.reason} onChange={e => setRefundForm({...refundForm, reason: e.target.value})} style={{width:'100%', marginBottom:'0.75rem', padding:'0.5rem'}}>
              <option value="">Select a reason *</option>
              <option value="damaged">Item arrived damaged</option>
              <option value="not_as_described">Item not as described</option>
              <option value="never_received">Never received</option>
              <option value="wrong_item">Wrong item sent</option>
              <option value="quality">Quality not acceptable</option>
              <option value="other">Other</option>
            </select>
            <textarea placeholder="Describe the issue in detail..." value={refundForm.description} onChange={e => setRefundForm({...refundForm, description: e.target.value})} rows={3} />
            <div style={{margin:'0.75rem 0'}}>
              <label style={{fontSize:'0.8rem', color:'var(--text-muted)', display:'block', marginBottom:'0.4rem'}}>📷 Attach photos (optional)</label>
              <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap', marginBottom:'0.5rem'}}>
                {refundForm.images.map((url, i) => <img key={i} src={url} alt="evidence" style={{width:60, height:60, objectFit:'cover', borderRadius:4}} />)}
              </div>
              <label className="btn-secondary" style={{cursor:'pointer', fontSize:'0.8rem', padding:'0.3rem 0.75rem'}}>
                {uploadingRefundImg ? 'Uploading...' : '+ Add Photo'}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={uploadingRefundImg} onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setUploadingRefundImg(true);
                  try {
                    const API_BASE = process.env.REACT_APP_API_URL || 'https://rj2psn7fng.execute-api.us-east-1.amazonaws.com/v1';
                    const token = localStorage.getItem('ts_customer_token');
                    const res = await fetch(`${API_BASE}/vendor/catalog/refunds/images`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ fileName: file.name, contentType: file.type }) });
                    const { uploadUrl, imageUrl } = await res.json();
                    await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
                    setRefundForm(prev => ({...prev, images: [...prev.images, imageUrl]}));
                  } catch { alert('Upload failed'); }
                  finally { setUploadingRefundImg(false); }
                }} />
              </label>
            </div>
            <div className="form-buttons">
              <button className="btn-primary" onClick={async () => {
                if (!refundForm.reason) { alert('Select a reason'); return; }
                try {
                  const API_BASE = process.env.REACT_APP_API_URL || 'https://rj2psn7fng.execute-api.us-east-1.amazonaws.com/v1';
                  const token = localStorage.getItem('ts_customer_token');
                  const res = await fetch(`${API_BASE}/customer/refunds`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ orderId: refundOrder.orderId, reason: refundForm.reason, description: refundForm.description, images: refundForm.images }) });
                  const data = await res.json();
                  if (!res.ok) throw data;
                  alert('Refund request submitted. We\'ll review within 48 hours.');
                  setOrders(prev => prev.map(o => o.orderId === refundOrder.orderId ? { ...o, status: 'refund_requested' } : o));
                  setRefundOrder(null);
                  setRefundForm({ reason: '', description: '', images: [] });
                } catch (e) { alert(e.error || 'Failed to request refund'); }
              }}>Submit Refund Request</button>
              <button className="btn-secondary" onClick={() => { setRefundOrder(null); setRefundForm({ reason: '', description: '', images: [] }); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerDashboard() {
  const { customer, loading, logoutCustomer } = useCustomerAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('commissions');
  const [commissions, setCommissions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', budget: '', timeline: '', woodSpecies: '', dimensions: '', inspirationNotes: '' });
  const [inspirationImages, setInspirationImages] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [error, setError] = useState('');
  const [viewingPitches, setViewingPitches] = useState(null);
  const [pitches, setPitches] = useState([]);
  useEffect(() => {
    if (loading) return;
    if (!customer) { navigate('/customer/login'); return; }
    loadCommissions();
  }, [customer, loading]);

  async function loadCommissions() {
    try {
      const data = await listCommissions();
      setCommissions(data.commissions || []);
    } catch (err) {
      setError('Failed to load commissions');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    try {
      await createCommission({ ...form, inspirationImages });
      setShowForm(false);
      setForm({ title: '', description: '', budget: '', timeline: '', woodSpecies: '', dimensions: '', inspirationNotes: '' });
      setInspirationImages([]);
      loadCommissions();
    } catch (err) {
      setError(err.error || 'Failed to create commission');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this commission request?')) return;
    try {
      await deleteCommission(id);
      loadCommissions();
    } catch (err) {
      setError('Failed to delete');
    }
  }

  useEffect(() => {
    if (viewingPitches) {
      getPitches(viewingPitches).then(data => setPitches(data.pitches || [])).catch(() => setPitches([]));
    }
  }, [viewingPitches]);

  async function handleAcceptPitch(pitchId) {
    if (!window.confirm('Accept this pitch? You will be redirected to pay.')) return;
    try {
      const data = await acceptPitch(viewingPitches, pitchId);
      if (data.url) window.location.href = data.url;
      else { setViewingPitches(null); loadCommissions(); }
    } catch (err) { alert(err.error || 'Failed to accept pitch'); }
  }

  if (loading || !customer) return null;

  return (
    <div className="dashboard-page">
      <Header />
      <div className="commissions-content">
        <div className="dashboard-header">
          <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
            <div>
              <h2>🏔️ Customer Dashboard</h2>
              <p className="dashboard-welcome">Welcome, {customer.displayName}</p>
            </div>
            <MembershipBadge tier={customer.subscriptionTier} />
          </div>
          <div className="dashboard-actions">
            <button className="btn-secondary" onClick={() => { logoutCustomer(); navigate('/'); }}>Sign Out</button>
          </div>
        </div>

        <div className="dashboard-tabs">
          <button className={`tab ${activeTab === 'commissions' ? 'active' : ''}`} onClick={() => setActiveTab('commissions')}>
            Commissions
          </button>
          <button className={`tab ${activeTab === 'offers' ? 'active' : ''}`} onClick={() => setActiveTab('offers')}>
            My Offers
          </button>
          <button className={`tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
            Order History
          </button>
          <button className={`tab ${activeTab === 'contributions' ? 'active' : ''}`} onClick={() => setActiveTab('contributions')}>
            My Contributions
          </button>
          <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
            Settings
          </button>
        </div>

        {error && <div className="auth-error">{error}</div>}

        {activeTab === 'commissions' && (
          <>
            <div className="catalog-toolbar">
              <button className="btn-primary" onClick={() => setShowForm(true)}>+ New Commission</button>
            </div>

            {showForm && (
              <div className="item-form-overlay">
                <div className="item-form commission-form">
                  <h3>Describe Your Dream Piece</h3>
                  <form onSubmit={handleSubmit}>
                    <input type="text" placeholder="Title (e.g., Live-edge dining table) *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
                    <textarea placeholder="Describe what you envision... *" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} required />
                    <input type="text" placeholder="Budget range (e.g., $500-$1,000)" value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} />
                    <input type="text" placeholder="Timeline (e.g., 4-6 weeks)" value={form.timeline} onChange={e => setForm({ ...form, timeline: e.target.value })} />
                    <input type="text" placeholder="Preferred wood species" value={form.woodSpecies} onChange={e => setForm({ ...form, woodSpecies: e.target.value })} />
                    <input type="text" placeholder="Dimensions (e.g., 72in x 36in x 30in)" value={form.dimensions} onChange={e => setForm({ ...form, dimensions: e.target.value })} />
                    <textarea placeholder="Inspiration notes (links, styles, references...)" value={form.inspirationNotes} onChange={e => setForm({ ...form, inspirationNotes: e.target.value })} rows={3} />
                    <div style={{margin:'0.75rem 0'}}>
                      <label style={{fontSize:'0.8rem', color:'var(--text-muted)', display:'block', marginBottom:'0.4rem'}}>📷 Reference photos (optional)</label>
                      <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap', marginBottom:'0.5rem'}}>
                        {inspirationImages.map((url, i) => <img key={i} src={url} alt="ref" style={{width:60, height:60, objectFit:'cover', borderRadius:4}} />)}
                      </div>
                      <label className="btn-secondary" style={{cursor:'pointer', fontSize:'0.8rem', padding:'0.3rem 0.75rem'}}>
                        {uploadingImg ? 'Uploading...' : '+ Add Photo'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={uploadingImg} onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploadingImg(true);
                          try {
                            const token = localStorage.getItem('ts_customer_token');
                            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
                            const API_BASE = process.env.REACT_APP_API_URL || 'https://rj2psn7fng.execute-api.us-east-1.amazonaws.com/v1';
                            const res = await fetch(`${API_BASE}/vendor/catalog/commissions/images`, { method: 'POST', headers, body: JSON.stringify({ fileName: file.name, contentType: file.type }) });
                            const { uploadUrl, imageUrl } = await res.json();
                            await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
                            setInspirationImages(prev => [...prev, imageUrl]);
                          } catch { alert('Upload failed'); }
                          finally { setUploadingImg(false); }
                        }} />
                      </label>
                    </div>
                    <div className="form-buttons">
                      <button type="submit" className="btn-primary">Post Request</button>
                      <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="commissions-grid">
              {commissions.length === 0 && <p className="empty-state">No commission requests yet. Post your first dream piece!</p>}
              {commissions.map(c => (
                <div className="commission-card" key={c.commissionId}>
                  <div className="commission-status">{c.status}</div>
                  <h3>{c.title}</h3>
                  <p className="commission-desc">{c.description}</p>
                  {c.budget && <p className="commission-meta">💰 {c.budget}</p>}
                  {c.timeline && <p className="commission-meta">⏱️ {c.timeline}</p>}
                  {c.woodSpecies && <p className="commission-meta">🪵 {c.woodSpecies}</p>}
                  {c.status === 'open' && <button className="btn-secondary" onClick={() => setViewingPitches(c.commissionId)}>View Pitches</button>}
                  {c.status === 'in_progress' && <p className="commission-meta" style={{color:'var(--accent)'}}>✅ Vendor accepted — work in progress</p>}
                  <button className="btn-delete" onClick={() => handleDelete(c.commissionId)}>Delete</button>
                </div>
              ))}
            </div>
          </>
        )}

        {viewingPitches && (
          <div className="item-form-overlay">
            <div className="item-form" style={{maxWidth:'600px'}}>
              <h3>🎯 Pitches Received</h3>
              {pitches.length === 0 && <p className="empty-state">No pitches yet. Vendors will see your commission on the board.</p>}
              {pitches.map(p => (
                <div key={p.pitchId} style={{borderBottom:'1px solid rgba(200,164,92,0.15)', padding:'1rem 0'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <strong>{p.vendorName}</strong>
                    <span style={{color:'var(--accent)', fontWeight:600}}>${(p.price / 100).toFixed(2)}</span>
                  </div>
                  <p style={{margin:'0.5rem 0', fontSize:'0.85rem', color:'var(--text-muted)'}}>{p.message}</p>
                  <span style={{fontSize:'0.75rem'}}>⏱ {p.estimatedDays} days • {p.status}</span>
                  {p.status === 'pending' && (
                    <button className="btn-primary" style={{marginLeft:'1rem', padding:'0.25rem 0.75rem', fontSize:'0.8rem'}} onClick={() => handleAcceptPitch(p.pitchId)}>Accept</button>
                  )}
                </div>
              ))}
              <div className="form-buttons" style={{marginTop:'1rem'}}>
                <button className="btn-secondary" onClick={() => setViewingPitches(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'offers' && (
          <CustomerOffers />
        )}

        {activeTab === 'orders' && (
          <CustomerOrders />
        )}

        {activeTab === 'contributions' && (
          <CustomerContributions />
        )}

        {activeTab === 'settings' && (
          <div className="settings-section">
            <h3>Account Settings</h3>
            <div className="settings-grid">
              <div className="settings-card">
                <h4>📋 Subscription</h4>
                <div className="subscription-manage">
                  <div className="sub-manage-card">
                    <span className={`sub-tier-badge ${customer.subscriptionTier || 'explorer'}`}>
                      {(customer.subscriptionTier || 'explorer').charAt(0).toUpperCase() + (customer.subscriptionTier || 'explorer').slice(1)}
                    </span>
                    <span className="sub-manage-text">Your current plan</span>
                  </div>
                  <div className="sub-actions">
                    {customer.subscriptionTier && customer.subscriptionTier !== 'explorer' ? (
                      <button className="btn-secondary" onClick={async () => {
                        try { const data = await openSubscriptionPortal(); window.location.href = data.url; }
                        catch (err) { setError('Failed to open subscription manager'); }
                      }}>Manage Plan</button>
                    ) : (
                      <>
                        <button className="btn-primary" onClick={async () => {
                          try { const data = await createSubscriptionCheckout('member'); window.location.href = data.url; }
                          catch(err) { setError('Failed to start checkout'); }
                        }}>Member — $4.99/mo</button>
                        <button className="btn-secondary" onClick={async () => {
                          try { const data = await createSubscriptionCheckout('insider'); window.location.href = data.url; }
                          catch(err) { setError('Failed to start checkout'); }
                        }}>Insider — $12.99/mo</button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h4>👤 Account Info</h4>
                <div className="settings-info">
                  <p><strong>Name:</strong> {customer.displayName}</p>
                  <p><strong>Email:</strong> {customer.email}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerDashboard;
